<template>
  <!------------------------------------------
    Map for the web version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-web-map">
    <MapTemplate :showLayerPanel="true" panelTitle="Show Districts or Counties" :mapContainerId="mapContainerId" :loading="loading" mapHeight="35rem" :layerPanel="layerPanelSetting">
      <v-radio-group
        id="panel-radio-options"
        v-model="additionalLayerSelection"
        row
        dense
        hide-details
      >
        <v-radio
          class="panel-radio"
          label="None"
          value="none"
        ></v-radio>
        <v-radio
          class="panel-radio"
          label="Show 119th congressional districts"
          value="cd"
        ></v-radio>
        <v-radio
          class="panel-radio"
          label="Show counties"
          value="county"
        ></v-radio>
      </v-radio-group>
      <div class="panel-radio-instruction">Hint: zoom in if not all labels are visible.</div>
    </MapTemplate>
    <div id="map-tooltip"></div>
  </div>
</template>

<script>
import MapTemplate from '@/components/map/MapTemplate'
import bboxes from '@/js/bboxes.js'
import states from '@/js/stFips.js'
import mapboxgl from "mapbox-gl"
import grantees from '@/js/grantees.js'

let map

export default {
  name: 'StateProfilesWebMap',

  components: { MapTemplate },

  props: {
    mapboxStyle: { type: String, required: false, default: "mapbox://styles/lsc-odga/cl8sv2oq8003i15p90nywwsu6?optimize=true" },
    interactive: { type: Boolean, required: false, default: true },
    selectedState: { type: String, required: true },
    colorPerServArea: { type: Array, required: true },
    officesPerGrantee: { type: Object, required: true },
    granteesByType: { type: Object, required: true },
    filterMapToOrg: { required: false } // rno to zoom the map to the rno and show its offices
  },

  data () {
    return {
      hoverInfo: null,
      grantees: grantees,
      loaded: false,
      newBBox: null, // When changing the bounding boxes, set the new bounding box to be newBBox to automatically re-fit the map
      defaultBoundsPadding: 25,
      accessToken: `${process.env.VUE_APP_MAPBOX_TOKEN}`,
      map: null,
      defaultBBox: [-124.736342, 24.521208, -66.945392, 49.382808], // contiguous US (used if no bbox provided)
      allowScrollZoom: false,
      loading: false,
      mapContainerId: 'map-container',
      layerSourcesForHoverInfo: {
        bfGeneral: '2024_bf_general_v2-fill-src',
        offices: 'offices'
      },
      additionalLayerSelection: 'none',
      layerPanelSetting: 0,
      officesLayersDrawn: [],
      layers: [
        { 
          tilesetId: 'lsc-odga.2024_bf_general_v2',
          loadOnStart: true,
          layerDefs: {
            'id': '2024_bf_general_v2-fill',
            'type': 'fill',
            'source': `2024_bf_general_v2-fill-src`,
            'source-layer': '2024_bf_general_v2',
            'paint': {
              'fill-opacity': 0.7,
              'fill-color': '#165991',
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.2024_bf_general_v2',
          loadOnStart: true,
          layerDefs: {
            'id': '2024_bf_general_v2',
            'type': 'line',
            'source': `2024_bf_general_v2-src`,
            'source-layer': '2024_bf_general_v2',
            'paint': {
              'line-width': 0.5,
              'line-color': '#f6f6f6'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.counties_2024_v2',
          loadOnStart: true,
          filterOn: 'state_fips',
          layerDefs: {
            'id': 'counties_2024_v2',
            'type': 'line',
            'source': `counties_2024_v2-src`,
            'source-layer': 'counties_2024_v2',
            'layout': {
              'visibility': 'none'
            },
            'paint': {
              'line-width': 1,
              'line-color': '#000',
              'line-dasharray': [2, 3]
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.counties_2024_v2',
          loadOnStart: true,
          filterOn: 'state_fips',
          layerDefs: {
            'id': 'counties_2024_v2_points',
            'type': 'symbol',
            'source': `counties_2024_v2_points-src`,
            'source-layer': 'counties_2024_v2_points',
            'layout': {
              'visibility': 'none',
              'text-field': ['get', 'name'],
              'text-allow-overlap': false,
              'text-font': [
                'Open Sans Bold',
                'Arial Unicode MS Bold'
              ],
              'text-size': 13,
              'text-transform': 'uppercase',
              'text-letter-spacing': 0.05,
            },
            'paint': {
              'text-color': '#1a1a1a'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'state_fips',
          layerDefs: {
            'id': '119th_congress',
            'type': 'line',
            'source': `119th_congress-src`,
            'source-layer': '119th_congress',
            'layout': {
              'visibility': 'none'
            },
            'paint': {
              'line-width': 1,
              'line-color': '#000',
              'line-dasharray': [2, 3]
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'state_fips',
          layerDefs: {
            'id': '119th_congress_points',
            'type': 'symbol',
            'source': `119th_congress_points-src`,
            'source-layer': '119th_congress_points',
            'layout': {
              'visibility': 'none',
              'text-field': ['get', 'cd_fips'],
              'text-allow-overlap': false,
              'text-font': [
                'Open Sans Bold',
                'Arial Unicode MS Bold'
              ],
              'text-size': 13,
              'text-transform': 'uppercase',
              'text-letter-spacing': 0.05,
            },
            'paint': {
              'text-color': '#1a1a1a'
            }
          }
        }
      ]
    }
  },

  computed: {
    bbox () { return this.newBBox === null || this.newBBox === undefined ? this.defaultBBox : this.newBBox },
    paddingOrDefault () { return this.padding === null || this.padding === undefined ? this.defaultBoundsPadding : this.padding },
    selectedStateAbbrev () { 
      if (this.selectedState !== '' && this.selectedState !== null && this.selectedState !== undefined) {
        return states.filter(x => x.fips === String(this.selectedState))[0].st
      } else {
        return ''
      }
    },
    filterStateField () { 
      return this.selectedStateAbbrev !== '' ? ["==", "st_abbrev", this.selectedStateAbbrev] : ['has', 'rno'] 
    },
    filterStateFipsField () { 
      return this.selectedStateAbbrev !== '' ? ["==", "state_fips", this.selectedState] : ['has', 'state_fips'] 
    },
    colorExpression () {
      let exp = ['match', ['get', 'serv_area']]
      this.colorPerServArea.forEach(function (d) {
        exp.push(
          d['area'],
          d['color']
        )
      })
      exp.push("#aaa") // default if no match
      return exp
    }
  },
  
  async mounted () {
    // Initialize the map
    mapboxgl.accessToken = this.accessToken
    this.$nextTick(() => {
      this.mapSetup()
    })
    this.newBBox = bboxes.states[this.selectedState]
    if (this.selectedState === '60') { // American Samoa
      this.newBBox = [-171.08652,-14.5329,-169.189,-13.8507]
    }
  },

  methods: {
    mapSetup: function() { 
      this.loading = true
      let padding = this.paddingOrDefault,
          allowScrollZoom = this.allowScrollZoom,
          mapContainerId = this.mapContainerId,
          component = this

      map = new mapboxgl.Map({
        container: mapContainerId,
        style: this.mapboxStyle,
        interactive: this.interactive,
        preserveDrawingBuffer: true
      })

      map.fitBounds( component.bbox, { padding: padding, duration:0 })

      if (allowScrollZoom === false) {
        map.scrollZoom.disable()
      }

      // ZOOM CONTROLS
      map.addControl(new mapboxgl.FullscreenControl({container: document.querySelector('#' + mapContainerId)}), 'top-left')
      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true
      })
      map.addControl(nav, 'top-left')

      // LAYERS
      map.on('load', function () {
        if (component.layers.length > 0) {
          component.initLayers()
        }

        if (component.hoverInfo === null) {
          component.initGetHoverInfo()
        }

        setTimeout(() => { 
          component.loading = false 
          component.loaded = true
        }, 100)
      })
    },

    async initLayers () {
      this.layers.forEach(layer => {
        if (layer.loadOnStart === true) {
          map.addSource(`${layer.layerDefs['source']}`, {
            type: 'vector',
            url: `mapbox://${layer.tilesetId}`
          })
          map.addLayer(layer.layerDefs)
        }
      })
      this.filterLayers()
    },

    fitBounds (bbox) {
      if (map) {
        let padding = this.paddingOrDefault
        map.fitBounds( bbox, { padding: padding, duration:0 })
      }
    },

    initGetHoverInfo () {
      map.on('mousemove', (e) => {
        const canvas = map.getCanvas();

        const features = map.queryRenderedFeatures(e.point)
        
        let hoverInfo = null
        
        // Show grantee name
        if (features.filter(x => x.layer.id === '2024_bf_general_v2-fill').length > 0) {
          let rno = features.filter(x => x.layer.id === '2024_bf_general_v2-fill')[0].properties.rno
          hoverInfo = this.getHoveredOrgName(String(rno)).slice(4) || null
        }

        const node = document.querySelector('#map-tooltip')
        if (hoverInfo !== null) {
          canvas.style.cursor = 'pointer'
          node.textContent = hoverInfo
          node.style.left = `${e.point.x + 10}px`
          node.style.top = `${e.point.y + 10}px`
          node.style.display = 'block'
        } else {
          canvas.style.cursor = ''
          node.style.display = 'none'
          node.textContent = ''
        }
      })

      // Clear the popup when leaving the map area
      if (document.getElementById('map-container') !== null) {
        document.getElementById('map-container').addEventListener('mouseleave', () => {
          document.getElementById('map-tooltip').style.display = 'none'
        })
      }
    },

    filterLayers () {
      const colorExp = this.colorExpression

      if (map) {
        this.layers.forEach(layer => {
          if (layer.filterOn === 'state_fips') {
            layer.layerDefs['filter'] = this.filterStateFipsField
            try {
              map.setFilter(layer.layerDefs['id'], this.filterStateFipsField)
            } catch (err) {
              console.log(err)
            }
          } else {
            layer.layerDefs['filter'] = this.filterStateField
            try {
              map.setFilter(layer.layerDefs['id'], this.filterStateField)
              if (layer.layerDefs['id'] === '2024_bf_general_v2-fill') {
                map.setPaintProperty(layer.layerDefs['id'], 'fill-color', colorExp)
              }
            } catch (err) {
              console.log(err)
            }
          }
        })
      }
    },

    async showMainOffices (rnoArray) {   
      for (const rno of rnoArray) {

        let o = this.officesPerGrantee[rno]
        let s = `offices_${rno}`

        // Remove the layer if already exists from prior draw
        const alreadyExists = map.getLayer(s)
        if(typeof alreadyExists !== 'undefined') {
          map.removeLayer(s).removeSource(s);
        }

        if (o !== null && o !== undefined) {
          map.addSource(s, {
            'type': 'geojson',
            'data': o
          })

          map.addLayer({
            'id': s,
            'type': 'symbol',
            'source': s,
            'layout': {
              'icon-image': ['get', 'type'],
              'icon-size': ['match', ['get', 'type'], 'Main', 1.1, 0],
              'icon-allow-overlap': true
            }
          })
        }

        this.officesLayersDrawn.push(rno)
      }
    },

    async showAllOffices (rno) {
      let o, s = `offices_${rno}`

      // Get the offices if don't yet have them (i.e. for states with many grantees the officesPerGrantee object will be empty)
      if (rno in this.officesPerGrantee) {
        o = this.officesPerGrantee[rno]
      } else {
        o = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/grantee/${rno}/offices.json`)
        .then(function(response) { 
          return response.json() 
        })
        .catch( err => { console.log(err) })
      }

      // Remove the layer if already exists from prior draw
      const alreadyExists = map.getLayer(s)
      if(typeof alreadyExists !== 'undefined') {
        map.removeLayer(s).removeSource(s);
      }

      if (o !== null && o !== undefined) {
        map.addSource(s, {
          'type': 'geojson',
          'data': o
        })

        map.addLayer({
          'id': s,
          'type': 'symbol',
          'source': s,
          'layout': {
            'icon-image': ['get', 'type'],
            'icon-size': ['match', ['get', 'type'], 'Main', 1.1, 0.5],
            'icon-allow-overlap': true
          }
        })
      }
      
      this.officesLayersDrawn.push(rno)
    },

    async removeOffices () {
      const rnos = this.officesLayersDrawn
      // Remove offices from prior map loads
      for (const rno of rnos) {
        let s = `offices_${rno}`
        const layerExists = map.getLayer(s)
        const sourceExists = map.getSource(s)
        if (typeof layerExists !== 'undefined') {
          map.removeLayer(s)
        }
        if (typeof sourceExists !== 'undefined') {
          map.removeSource(s)
        }
        this.officesLayersDrawn = this.officesLayersDrawn.filter(x => x !== rno)
      }
    },

    addOfficesDispatcher (rnoArray) {
      if (rnoArray.length === 1) {
        // One grantee => display all offices
        this.showAllOffices(rnoArray)
      } else if (rnoArray.length < 4) {
        // 1 to 3 grantees => display main offices only
        this.showMainOffices(rnoArray)
      } else {
        return
      }
    },

    async zoomToSelectedGrantee (bfArea) {
      const resp = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/bounds/service_areas/2024/${bfArea}/bounds.json`)
      .then(function (response) {
        return response.json()
      })
      .catch((err) => { 
        console.log(err) 
      })
      
      return resp['bbox']
    },

    getHoveredOrgName (rno) {
      return this.grantees.filter(x => x.value === rno)[0].text
    }
  },
  

  watch: {
    newBBox(to) {
      this.fitBounds(to)
    },
    
    loaded (to) {
      if (to === true) {
        if (Object.keys(this.officesPerGrantee).length > 0) {
          this.addOfficesDispatcher(Object.keys(this.officesPerGrantee))
        }
        // Close the additional layers panel after its contents are created
        this.layerPanelSetting = 1
      }
    },

    officesPerGrantee (to) {
      if (this.loaded === true) {
        this.removeOffices()
        this.addOfficesDispatcher(Object.keys(to))
      }
    },

    async filterMapToOrg (to) {
      const rno = to
      // If activating or changing the filter
      if (rno !== null) {
        // Remove any existing offices
        this.removeOffices()
        // Zoom to selected grantee
        const bfArea = this.granteesByType.bfGeneral.filter(x => x.recipient_id === rno)[0].serv_area
        this.newBBox = await this.zoomToSelectedGrantee(bfArea)
        // Show all offices for the selected grantee
        this.showAllOffices(rno)
      }

      // If deactivating the filter
      if (rno === null) {
        // Back to the default offices view (whether all, none, or main only)
        this.removeOffices()
        this.addOfficesDispatcher(Object.keys(this.officesPerGrantee))
        // Re-show all areas
        this.filterLayers()
        // Reset zoom
        this.newBBox = bboxes.states[this.selectedState]
      }
    },

    additionalLayerSelection (to, from) {
      // Hide previously selected
      if (from === 'county') {
        this.layers.forEach(layer => {
          if (layer.tilesetId === 'lsc-odga.counties_2024_v2') {
              map.setLayoutProperty(layer.layerDefs['id'], 'visibility', 'none')
          }
        })
      } else if (from === 'cd' ){
        this.layers.forEach(layer => {
          if (layer.tilesetId === 'lsc-odga.119th_congress') {
              map.setLayoutProperty(layer.layerDefs['id'], 'visibility', 'none')
          }
        })
      }

      // Show newly selected
      if (to === 'county') {
        this.layers.forEach(layer => {
          if (layer.tilesetId === 'lsc-odga.counties_2024_v2') {
              map.setLayoutProperty(layer.layerDefs['id'], 'visibility', 'visible')
          }
        })
      } else if (to === 'cd') {
        this.layers.forEach(layer => {
          if (layer.tilesetId === 'lsc-odga.119th_congress') {
              map.setLayoutProperty(layer.layerDefs['id'], 'visibility', 'visible')
          }
        })
      }

      // Set service area boundaries to no line so that the lines don't compete with the county or CD boundaries (or reset otherwise)
      if (to !== 'none') {
        map.setPaintProperty('2024_bf_general_v2', 'line-width', 0)
      } else {
        map.setPaintProperty('2024_bf_general_v2', 'line-width', 0.5)
      }
    },
  }
}
</script>

<style lang="scss">
@import '@/scss/vars.scss';
#congr-state-web-map {
  position: relative;

  #map-container {
    border-radius: 0.5rem;
  }
  #map-tooltip {
    position: absolute;
    padding: 0.25rem 0.5rem;
    background-color: rgba(250, 250, 250, 0.8);
    border-radius: 0.5rem;
    pointer-events: none;
  }
}
#panel-radio-options {
  .v-radio {
    align-items: flex-start;
  }

  .panel-radio label {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
}
.panel-radio-instruction {
  font-style: italic;
  font-size: 0.8rem;
  color: #5a5a5a;
}
</style>